// Public/private split for Top 8 tiles.
//
// The public profile document (users/{uid}.top8) is world-readable, so it must
// never contain private action targets: phone numbers, sms values, or
// third-party social URLs. Those are owner-only shortcuts and live in
// users/{uid}/privateTileActions/{tileId}, readable only by the owner.
//
// The ONLY action target that is safe to publish is a link to another Your Top 8
// profile (a YT8 username), because that destination is already public.

// Splits a full editable tile into the public-safe tile + the owner-private
// action document.
export function splitTile(tile) {
  const conns = tile.connections || [];
  // A tile is a real public YT8 link ONLY if it has an actual username. A bare
  // PROFILE connection with no username is malformed (e.g. legacy migrate
  // defaults) and must NOT be treated as a public, clickable link.
  const yt8 = (tile.yt8Username && String(tile.yt8Username).trim()) ? tile.yt8Username : null;
  const hasYt8 = !!yt8;
  const hasPrivateAction = conns.some(c => c.type === 'TEXT' || c.type === 'SOCIAL');

  const publicTile = {
    id: tile.id,
    displayName: tile.displayName || '',
    // A registered tile shows that user's own (moderated) profile photo. A
    // prospect's photo is owner-uploaded, so it stays owner-private (visitors
    // see an initial) — that keeps the only public user image to real profile
    // pics, which is the surface we moderate.
    photoURL: hasYt8 ? (tile.photoURL || null) : null,
    isRegisteredUser: !!tile.isRegisteredUser,
    yt8Username: yt8,
    connectionStatus: tile.connectionStatus || null,
    // A visitor can only ever act on a tile that links to a YT8 profile.
    publicActionAvailable: hasYt8,
    hasPrivateAction,
    primaryAction: hasYt8 ? 'PROFILE' : null,
    primaryValue: hasYt8 ? yt8 : null,
    // Keep a connections array (private values redacted) so the editor's
    // migrateEntry recognizes this as new-format data and never re-mangles it.
    connections: conns.map(c => ({
      id: c.id,
      type: c.type,
      value: c.type === 'PROFILE' && hasYt8 ? c.value : null,
    })),
  };

  const privateAction = {
    tileId: tile.id,
    primaryAction: tile.primaryAction || null,
    primaryValue: tile.primaryValue ?? null,
    connections: conns.map(c => ({ id: c.id, type: c.type, value: c.value })),
    // Owner-private prospect photo (registered tiles keep theirs on the public tile).
    photoURL: hasYt8 ? null : (tile.photoURL || null),
    updatedAt: new Date(),
  };

  return { publicTile, privateAction };
}

// Reassembles the full editable tile from the public tile + its private action
// doc (used by the owner in the editor and on their own profile).
export function mergeTile(publicTile, privateAction) {
  if (privateAction) {
    return {
      ...publicTile,
      primaryAction: privateAction.primaryAction || publicTile.primaryAction,
      primaryValue: privateAction.primaryValue ?? publicTile.primaryValue,
      connections: privateAction.connections || [],
      // Restore the owner-private prospect photo for the owner's own views.
      photoURL: publicTile.photoURL || privateAction.photoURL || null,
    };
  }
  // No private doc yet (e.g. a tile that only links to a YT8 profile, or
  // pre-migration data). Reconstruct a minimal connection set from public data.
  const connections = publicTile.yt8Username
    ? [{ id: `${publicTile.id}_p`, type: 'PROFILE', value: publicTile.yt8Username }]
    : [];
  return { ...publicTile, connections };
}

// True if a public tile (or legacy raw tile) still carries private values inline,
// meaning it needs migrating into the public/private split.
export function tileHasInlinePrivateValue(tile) {
  const conns = tile.connections || [];
  if (conns.some(c => (c.type === 'TEXT' || c.type === 'SOCIAL') && c.value)) return true;
  if ((tile.primaryAction === 'TEXT' || tile.primaryAction === 'SOCIAL') && tile.primaryValue) return true;
  return false;
}
